import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  buildUnauthorizedUrl,
  isDevLoginSessionLossCode,
  signedFetch,
} from '../super-picker/services/api';
import MethodChooser, { CommunicationMethod } from './pages/MethodChooser';
import WhatsAppForm from './pages/WhatsAppForm';
import WhatsAppTemplateList from './pages/WhatsAppTemplateList';
import EmailForm from './pages/EmailForm';
import EmailTemplateList from './pages/EmailTemplateList';
import { WhatsAppTemplate } from './services/api';
import { EmailTemplate } from './services/emailApi';

type AccessState = 'checking' | 'granted' | 'denied';
type WhatsAppView = 'list' | 'create' | 'edit';
type EmailView = 'list' | 'create' | 'edit';

export function CommunicationApp() {
  const [accessState, setAccessState] = useState<AccessState>('checking');
  const [method, setMethod] = useState<CommunicationMethod | null>(null);
  const [whatsappView, setWhatsappView] = useState<WhatsAppView>('list');
  const [editTarget, setEditTarget] = useState<WhatsAppTemplate | null>(null);
  const [emailView, setEmailView] = useState<EmailView>('list');
  const [emailEditTarget, setEmailEditTarget] = useState<EmailTemplate | null>(null);

  useEffect(() => {
    let mounted = true;

    const verifyAccess = async () => {
      try {
        // Communication is its own access plane: the caller must hold an
        // active `TransformationAppUserAccess` row with `App='COMMUNICATION'`
        // and `Role='ADMIN'`. Super Picker admin alone is not enough.
        const response = await signedFetch('/api/communication/access');
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const errorCode = typeof data?.code === 'string' ? data.code : undefined;
          const devLoginRedirectHandled =
            response.status === 401 && isDevLoginSessionLossCode(errorCode);
          if (mounted) {
            setAccessState('denied');
            if (!devLoginRedirectHandled) {
              window.location.replace(
                buildUnauthorizedUrl(errorCode || 'NO_COMMUNICATION_ACCESS', {
                  app: 'communication',
                }),
              );
            }
          }
          return;
        }

        if (mounted) setAccessState('granted');
      } catch {
        if (mounted) {
          setAccessState('denied');
          window.location.replace(
            buildUnauthorizedUrl('NO_COMMUNICATION_ACCESS', { app: 'communication' }),
          );
        }
      }
    };

    verifyAccess();
    return () => {
      mounted = false;
    };
  }, []);

  if (accessState === 'checking') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Checking admin access…</Typography>
      </Box>
    );
  }

  if (accessState === 'denied') {
    return null;
  }

  if (method === 'whatsapp') {
    if (whatsappView === 'create') {
      return (
        <WhatsAppForm
          onBack={() => setWhatsappView('list')}
          onCreated={() => {
            // Stay on the form to show the success alert; user can click
            // "Go to templates" / Back to return.
          }}
        />
      );
    }
    if (whatsappView === 'edit' && editTarget) {
      return (
        <WhatsAppForm
          editTemplate={editTarget}
          onBack={() => {
            setEditTarget(null);
            setWhatsappView('list');
          }}
          onUpdated={() => {
            // Stay on the form to show success alert; back returns to list.
          }}
        />
      );
    }
    return (
      <WhatsAppTemplateList
        onBack={() => {
          setMethod(null);
          setWhatsappView('list');
        }}
        onCreate={() => setWhatsappView('create')}
        onEdit={(template) => {
          setEditTarget(template);
          setWhatsappView('edit');
        }}
      />
    );
  }

  if (method === 'email') {
    if (emailView === 'create') {
      return (
        <EmailForm
          onBack={() => setEmailView('list')}
          onCreated={() => {
            // Stay on the form to show the success alert; user can navigate back.
          }}
        />
      );
    }
    if (emailView === 'edit' && emailEditTarget) {
      return (
        <EmailForm
          editTemplate={emailEditTarget}
          onBack={() => {
            setEmailEditTarget(null);
            setEmailView('list');
          }}
          onUpdated={() => {
            // Stay on the form to show success alert.
          }}
        />
      );
    }
    return (
      <EmailTemplateList
        onBack={() => {
          setMethod(null);
          setEmailView('list');
        }}
        onCreate={() => setEmailView('create')}
        onEdit={(template) => {
          setEmailEditTarget(template);
          setEmailView('edit');
        }}
      />
    );
  }

  return (
    <MethodChooser
      onSelect={(m) => {
        setMethod(m);
        setWhatsappView('list');
        setEmailView('list');
      }}
    />
  );
}

export default CommunicationApp;
